@import "../../sass/mixins/focus";

/**
    @prop --uic-wizard-header-step-background-color: background color
    @prop --uic-wizard-header-step-focus-color: focus color
    @prop --uic-wizard-header-step-disabled-number-bg-color: disabled number background color
    @prop --uic-wizard-header-step-disabled-label-color: disabled label color
    @prop --uic-wizard-header-step-hover-number-border-color: hover number border color
    @prop --uic-wizard-header-step-hover-number-color: hover number color
    @prop --uic-wizard-header-step-active-bg-color: active background color
    @prop --uic-wizard-header-step-active-number-bg-color: active number background color
    @prop --uic-wizard-header-step-active-number-border-color: active number border color
    @prop --uic-wizard-header-step-active-number-color: active number color
    @prop --uic-wizard-header-step-active-number-highlight-color: active number highlight color
    @prop --uic-wizard-header-step-selected-bg-color: selected background color
    @prop --uic-wizard-header-step-selected-line-color: selected line color
    @prop --uic-wizard-header-step-selected-number-bg-color: selected number bg color
    @prop --uic-wizard-header-step-selected-number-color: selected number color
    @prop --uic-wizard-header-step-selected-label-color: selected label color
    @prop --uic-wizard-header-step-flyout-indicator-color: flyout indicator color
    @prop --uic-wizard-header-step-number-bg-color: number background color
    @prop --uic-wizard-header-step-number-color: number color
    @prop --uic-wizard-header-step-flag-width: flag icon width
    @prop --uic-wizard-header-step-flag-height: flag icon height
    @prop --uic-wizard-header-step-flag-offset-left: flag offset left
    @prop --uic-wizard-header-step-flag-offset-top: flag offset top
    @prop --uic-wizard-header-step-base-height-vertical: step height in vertical layout, might be larger for multiline
    @prop --uic-wizard-header-step-number-width: number rectangle width
    @prop --uic-wizard-header-step-number-height: number rectangle height
    @prop --uic-wizard-header-step-flyout-indicator-size: flyout indicator icon size
    @prop --uic-wizard-header-step-flyout-indicator-padding-left: flyout indicator padding left
    @prop --uic-wizard-header-step-number-margin: header step number margin. top when horizontal layout, right when vertical
    @prop --uic-wizard-header-step-padding-horizontal-left-right: left and right padding in horizontal layout
    @prop --uic-wizard-header-step-padding-horizontal-top-bottom: top and bottom padding in horizontal layout
    @prop --uic-wizard-header-step-padding-vertical: 4 axis padding in vertical layout
    @prop --uic-wizard-header-step-line-width: width of the line between steps
    @prop --uic-wizard-header-step-line-width-active: width of the line between steps when active (steps before current one)
    @prop --uic-wizard-header-step-line-gap-horizontal: gap between line and number rectangle in horizontal layout
    @prop --uic-wizard-header-step-line-gap-vertical: gap between line and number rectangle in vertical layout
    @prop --uic-wizard-header-step-line-offset-horizontal: offset of the line from the number rectangle center to the bottom
*/
uic-wizard-header-step {
    display: flex;
    box-sizing: border-box;
    position: relative;
    flex-direction: column;
    flex-grow: 1;
    align-items: center;
    padding: var(--uic-wizard-header-step-padding-horizontal-top-bottom) var(--uic-wizard-header-step-padding-horizontal-left-right);
    background-color: var(--uic-wizard-header-step-background-color);
    cursor: pointer;

    // define the same flex basis for each step so they distribute the space equally
    flex-basis: var(--uic-wizard-header-step-number-width);

    // modifiers
    @include focus-visible() using ($slot) {
        @if $slot==focus {
            outline: none;

            .uic-wizard-header-step__number {
                border: 2px solid var(--uic-wizard-header-step-focus-color);
            }
        }

        @if $slot==focusReset {
            outline: none;

            .uic-wizard-header-step__number {
                border: 0;
            }
        }

        @if $slot==focusVisible {
            outline: none;

            .uic-wizard-header-step__number {
                border: 2px solid var(--uic-wizard-header-step-focus-color);
            }
        }
    }

    &[disabled] {
        pointer-events: none;

        .uic-wizard-header-step__number {
            background-color: var(--uic-wizard-header-step-disabled-number-bg-color);
        }

        .uic-wizard-header-step__label {
            color: var(--uic-wizard-header-step-disabled-label-color);
        }
    }

    &:hover {
        .uic-wizard-header-step__number {
            border: 1px solid var(--uic-wizard-header-step-hover-number-border-color);
            color: var(--uic-wizard-header-step-hover-number-color);
        }
    }

    &:active {
        background: var(--uic-wizard-header-step-active-bg-color);

        .uic-wizard-header-step__number {
            background-color: var(--uic-wizard-header-step-active-number-bg-color);
            border-color: var(--uic-wizard-header-step-active-number-border-color);
            color: var(--uic-wizard-header-step-active-number-color);

            &::after {
                content: "";
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
                background: var(--uic-wizard-header-step-active-number-highlight-color);
            }
        }
    }

    &[selected] {
        background-color: var(--uic-wizard-header-step-selected-bg-color);

        .uic-wizard-header-step__number {
            background: var(--uic-wizard-header-step-selected-number-bg-color);
            color: var(--uic-wizard-header-step-selected-number-color);
            font-weight: bold;
        }

        .uic-wizard-header-step__label {
            color: var(--uic-wizard-header-step-selected-label-color);
            font-weight: bold;
        }

        .uic-wizard-header-step__label-wrapper--with-flyout .uic-wizard-header-step__flyout-indicator {
            visibility: visible;
        }
    }

    .uic-wizard-header-step__number {
        display: flex;
        box-sizing: border-box;
        position: relative;
        align-items: center;
        justify-content: center;
        border: 1px solid transparent;
        background: var(--uic-wizard-header-step-number-bg-color);
        color: var(--uic-wizard-header-step-number-color);
        font-size: var(--uic-font-size-normal);

        width: var(--uic-wizard-header-step-number-width);
        height: var(--uic-wizard-header-step-number-height);

        margin-top: var(--uic-wizard-header-step-number-margin);
        flex-shrink: 0;

        // flags
        .uic-wizard-header-step__flag {
            --uic-icon-width: var(--uic-wizard-header-step-flag-width);
            --uic-icon-height: var(--uic-wizard-header-step-flag-height);
            position: absolute;
            top: var(--uic-wizard-header-step-flag-offset-top);
            left: var(--uic-wizard-header-step-flag-offset-left);
        }
    }

    .uic-wizard-header-step__label-wrapper {
        --horzizontal-margin: calc(
            var(--uic-wizard-header-step-flyout-indicator-size) + var(--uic-wizard-header-step-flyout-indicator-padding-left)
        );
        display: flex;
        flex-direction: row;
        justify-content: center;

        margin: 2px var(--horzizontal-margin) 0 var(--horzizontal-margin);

        &--with-flyout {
            margin: 2px 0 0 var(--horzizontal-margin);
        }

        .uic-wizard-header-step__label {
            text-align: center;
            flex-grow: 0;

            display: block;
            text-overflow: ellipsis;
            overflow: hidden;

            /* stylelint-disable declaration-property-value-disallowed-list, value-no-vendor-prefix, property-no-vendor-prefix */
            /* webkit not supported by IE, but currently no other good solution is available for multiline text with ellipsis.  */
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            /* stylelint-enable declaration-property-value-disallowed-list, value-no-vendor-prefix, property-no-vendor-prefix */
        }

        // flyout indicator
        .uic-wizard-header-step__flyout-indicator {
            --uic-icon-width: var(--uic-wizard-header-step-flyout-indicator-size);
            --uic-icon-height: var(--uic-wizard-header-step-flyout-indicator-size);
            visibility: hidden;
            margin-top: -1px;
            padding-left: var(--uic-wizard-header-step-flyout-indicator-padding-left);
            fill: var(--uic-wizard-header-step-flyout-indicator-color);
            width: 100%;
            align-self: center;
            flex-grow: 0;
            flex-shrink: 0;
            flex-basis: var(--uic-wizard-header-step-flyout-indicator-size);
        }
    }

    .uic-wizard-header-step__flyout {
        --uic-flyout-content-min-height: 0;
        --uic-flyout-max-width: 200%;
        pointer-events: none;

        uic-button {
            pointer-events: auto;
        }
    }

    // Lines
    &:not(:first-child)::before {
        content: "";
        width: calc(50% - var(--uic-wizard-header-step-number-width) / 2 - var(--uic-wizard-header-step-line-gap-horizontal));
        height: var(--uic-wizard-header-step-line-width-active);
        position: absolute;
        top: calc(
            4px + var(--uic-wizard-header-step-number-margin) + var(--uic-wizard-header-step-padding-horizontal-top-bottom) +
                var(--uic-wizard-header-step-number-height) / 2
        );
        left: 0;
        background: var(--uic-color-blue2);
    }

    &:not(:last-child)::after {
        content: "";
        width: calc(50% - var(--uic-wizard-header-step-number-width) / 2 - var(--uic-wizard-header-step-line-gap-horizontal));
        height: var(--uic-wizard-header-step-line-width-active);
        position: absolute;
        top: calc(
            var(--uic-wizard-header-step-line-offset-horizontal) + var(--uic-wizard-header-step-number-margin) +
                var(--uic-wizard-header-step-padding-horizontal-top-bottom) + var(--uic-wizard-header-step-number-height) / 2
        );
        right: 0;
        background: var(--uic-color-blue2);
    }

    /* stylelint-disable selector-no-qualifying-type */
    &[selected] ~ uic-wizard-header-step {
        &::before,
        &::after {
            height: var(--uic-wizard-header-step-line-width);
            background: var(--uic-color-grey2);
        }
    }

    /* stylelint-enable selector-no-qualifying-type */

    &[selected] {
        &::after {
            height: var(--uic-wizard-header-step-line-width);
            background: var(--uic-color-grey2);
        }
    }

    &[vertical] {
        flex-direction: row;
        justify-content: flex-start;
        padding: var(--uic-wizard-header-step-padding-vertical);

        height: 100%;
        flex-basis: var(--uic-wizard-header-step-base-height-vertical);

        .uic-wizard-header-step__number {
            margin: 0 var(--uic-wizard-header-step-number-margin) 0 0;
        }

        .uic-wizard-header-step__label-wrapper {
            width: 100%;
            justify-content: left;
            margin: 0;

            &--with-flyout {
                justify-content: space-between;
                margin: 0;
            }
        }

        .uic-wizard-header-step__label {
            margin-top: 0;
            flex-grow: 0;
            text-align: left;
        }

        &:not(:first-child)::before {
            width: var(--uic-wizard-header-step-line-width-active);
            top: 0;
        }

        &:not(:last-child)::after {
            width: var(--uic-wizard-header-step-line-width-active);
        }

        // modifiers
        /* stylelint-disable selector-no-qualifying-type */
        &[selected] ~ uic-wizard-header-step {
            &::before,
            &::after {
                width: var(--uic-wizard-header-step-line-width);
            }
        }

        /* stylelint-enable selector-no-qualifying-type */

        &[selected] {
            &::after {
                width: var(--uic-wizard-header-step-line-width);
            }
        }

        &:not(:first-child)::before,
        &:not(:last-child)::after {
            height: calc(50% - var(--uic-wizard-header-step-number-height) / 2 - var(--uic-wizard-header-step-line-gap-vertical));
            left: var(--uic-wizard-header-step-line-left-vertical);
        }

        &:not(:last-child)::after {
            top: calc(50% + var(--uic-wizard-header-step-number-height) / 2 + var(--uic-wizard-header-step-line-gap-vertical));
        }
    }
}
